import { useNavigate, useParams } from "react-router-dom";

import { GridRenderCellParams } from "@mui/x-data-grid";
import { formatShortDate, useClientStatusLabel } from "@shared";
import { useQuery } from "@tanstack/react-query";

import { volunteersApi } from "@/api";
import { ClientLink } from "@/common/components/client-link/ClientLink";
import { EllipsisText } from "@/common/components/ellipsis-text/EllipsisText";
import { createColumns } from "@/common/components/table/DataTable.utils";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { QueryKey } from "@/query/Query";
import { ClientMasterDataTab, clientPath } from "@/router/ClientRoutes";
import { VolunteerByIdParams } from "@/router/SystemSettingsRoutes";
import { useTranslation } from "@/translations";

interface AssignedClientRow {
  clientId: string;
  clientName: string;
  caseNumber: string;
  status: string | null;
  assignedAtUtc: Date;
}

const volunteerAssignmentPath = (clientId: string) =>
  clientPath.byMasterDataTab({ id: clientId, tab: ClientMasterDataTab.VolunteerAssignment });

export function useVolunteerAssignedClients() {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const navigate = useNavigate();
  const { id } = useParams<VolunteerByIdParams>();
  const statusLabelOf = useClientStatusLabel();

  const { data, isLoading } = useQuery({
    queryKey: [QueryKey.VolunteerAssignedClients, id],
    queryFn: () => volunteersApi.getVolunteerAssignedClients({ id: id ?? "" }),
    enabled: !!id,
    meta: { loadingScope: "pageContent" },
  });

  const rows: AssignedClientRow[] = (data ?? []).map((assignment) => ({
    clientId: assignment.clientId,
    clientName: `${assignment.lastName}, ${assignment.firstName}`,
    caseNumber: assignment.caseNumber,
    status: assignment.status,
    assignedAtUtc: assignment.assignedAtUtc,
  }));

  const columns = createColumns<AssignedClientRow>([
    {
      field: "clientName",
      headerName: t("volunteers.assignedClients.table.client"),
      flex: 1,
      renderCell: (params: GridRenderCellParams<AssignedClientRow>) => (
        <ClientLink
          clientId={params.row.clientId}
          name={params.row.clientName}
          variant="body2"
          masterDataTab={ClientMasterDataTab.VolunteerAssignment}
        />
      ),
    },
    {
      field: "caseNumber",
      headerName: t("volunteers.assignedClients.table.caseNumber"),
      flex: 1,
      renderCell: (params) => <EllipsisText value={params.value as string} variant="body2" />,
    },
    {
      field: "status",
      headerName: t("volunteers.assignedClients.table.status"),
      flex: 1,
      renderCell: (params) => <EllipsisText value={statusLabelOf(params.value as string | null)} variant="body2" />,
    },
    {
      field: "assignedAtUtc",
      headerName: t("volunteers.assignedClients.table.assignedAt"),
      type: "dateTime",
      flex: 1,
      renderCell: (params: GridRenderCellParams<AssignedClientRow>) => (
        <EllipsisText value={formatShortDate(params.row.assignedAtUtc, locales[language])} variant="body2" />
      ),
    },
  ]);

  const openClient = (clientId: string) => void navigate(volunteerAssignmentPath(clientId));

  return { rows, isLoading, columns, openClient };
}
